/**
 * Mini-Project goal view derivation (DEV-1075 Phase 7.1).
 *
 * Bridges unified KpiConfig + MissionMetricPoint data (post-DEV-1075
 * source of truth) into the goal-card-friendly shape that
 * MiniProjectGoalCard, GoalsSection, MissionModal, and StatusWriter
 * still consume.
 *
 * Keeping the downstream component shape stable in Phase 7.1 means
 * the card UI doesn't need to change in this commit — only the data
 * source. Phase 7.3 then extends the view with the new fields
 * (description, photos, included_in_rollup, deadline) that have no
 * pre-DEV-1075 analogue.
 *
 * Why a view, not raw KpiConfig? Two reasons:
 *   1. currentValue is DERIVED from the latest value-bearing
 *      mission_metric_point — it's not a stored column on kpi_config.
 *      The card needs the materialized number to render the progress
 *      bar; consumers shouldn't re-derive it every render.
 *   2. primitive is INFERRED from mission_type_key + uom (see
 *      goalPrimitiveMapping.ts). The card switches rendering on it.
 *      Inferring once at the boundary is cheaper and clearer than
 *      threading inference through every consumer.
 *
 * The shape mirrors src/api/miniProjects.ts MiniProjectGoal field
 * names so the migration of card/section/modal code is a minimal-
 * diff retargeting (swap the import, keep the field access).
 */

import type { KpiConfigResponse } from '../../api/kpiConfigs';
import type { MissionMetricPointResponse } from '../../api/missionMetrics';
import {
  inferPrimitiveFromUnified,
  type MpGoalPrimitive,
} from './goalPrimitiveMapping';

/**
 * Goal-card-friendly view of a single Mini-Project Mission sub-KPI.
 * Derived from one KpiConfig row + the slice of mission_metric_points
 * that share its (mini_project_guid, sub_kpi_key).
 *
 * Field names match the legacy MiniProjectGoal interface so consumers
 * port via a one-line import swap.
 */
export interface MiniProjectGoalView {
  /** The unified row's instance_guid — preserves the legacy goal_guid
   *  identifier so deep links and existing state shapes work unchanged. */
  goalGuid: string;
  miniProjectGuid: string;
  /** kpi_config.instance_guid for unified writes (same value as goalGuid;
   *  exposed separately so the card knows it can PUT/DELETE via
   *  /api/kpiconfigs/{configId} using the kpi_config row's actual id). */
  kpiConfigId: string;
  nameKey: string;
  primitive: MpGoalPrimitive;
  unitKey: string | null;
  targetValue: number | null;
  /** Derived from latest value-bearing mission_metric_point. 0 when
   *  no numeric data has been recorded yet. */
  currentValue: number;
  required: boolean;
  /** Definition only (label + ordering). Per-item completion state lives
   *  in mission_metric_points with item_id set — see derivedChecklist
   *  below for the materialized current state. */
  checklistItems: string | null;
  /** Materialized per-item completion state for primitive='checklist'.
   *  Each item is { label, checked, itemId } where itemId matches the
   *  mp_item_<index> scheme used by the Phase 3 backfill and Phase 7
   *  writes. Null for non-checklist goals. */
  derivedChecklist: DerivedChecklistItem[] | null;
  position: number;
  currencyCode: string | null;
  /** Latest of (kpi_config.updated_at, latest point.created_at). */
  lastUpdatedAt: string;
  createdAt: string;
  updatedAt: string;

  // ── Interface-compat shims (mirror legacy MiniProjectGoal shape) ──
  // These exist so downstream consumers that still type against the
  // legacy MiniProjectGoal interface (StatusWriter, MissionModal) accept
  // a MiniProjectGoalView without changes. Both are post-unification
  // information losses Skip + I knowingly accepted in Phase 7.1:
  //
  //   playbookGoalKey: the Phase 3 backfill didn't carry it over to
  //   kpi_config (there's no column for it). Consequence: the template-
  //   lock UI in EditGoalForm goes inert — all goals appear non-template
  //   and editable. Re-introducing the lock needs either a kpi_config
  //   schema column for the playbook key or a different template-tracking
  //   mechanism. Out of Phase 7.1 scope.
  //
  //   lastUpdatedByUserGuid: source isn't tracked through the unified
  //   write path uniformly (kpi_config tracks updated_by, but per-point
  //   writes track created_by separately). The legacy field carried the
  //   most recent goal-level write; the unified equivalent would need a
  //   max() across both signals. Leaving undefined for now — no UI
  //   currently consumes this field.
  playbookGoalKey?: string | null;
  lastUpdatedByUserGuid?: string | null;

  // ── DEV-1075 fields surfaced in Phase 7.3a for the new goal edit tray ──
  // The card's compact render still doesn't show these directly; they
  // round-trip through the edit tray (description editor, photo strip,
  // include-in-rollup checkbox, deadline picker). Stored on the
  // underlying KpiConfig row by Phase 5.4's controller wiring.

  /** TipTap JSON serialized as a string. null/empty → no description. */
  descriptionRich: string | null;
  /** Server-derived plain projection. Read-only from the client; used
   *  for card snippet rendering (Phase 7.3 follow-up) and future
   *  semantic search. */
  descriptionPlain: string | null;
  /** JSON array of {storageKey, caption, position}. null/empty → no
   *  photos. Same shape StatusPhotoStrip expects. */
  descriptionPhotos: string | null;
  /** When false the goal's color is still rendered on its own card but
   *  excluded from the Mission rollup's weighted average. Defaults
   *  true server-side; passed through for the edit tray to read. */
  includedInRollup: boolean;
  /** ISO date 'YYYY-MM-DD' or null. When set, the Mission engine
   *  shifts color toward yellow/red as the deadline approaches without
   *  completion (independent of qualitative assessment). */
  deadlineDate: string | null;

  // ── DEV-1116: latest qualitative assessment, derived at the view
  // boundary so the Mission KPI dot (computeKpiStatus) and any other
  // summary consumer can read it without re-deriving from raw points.
  //
  // One of the 5-state ladder values ('ahead' / 'on_track' / 'behind' /
  // 'blocked' / 'done') or null when no qualitative assessment has been
  // recorded yet. Determined by the latest reportingDate among
  // mission_metric_points where .assessment is set for this goal.
  /** Latest Lead-recorded qualitative assessment, or null when none. */
  latestAssessment: string | null;

  /** DEV-822 — the raw mission_type_key, surfaced so consumers can apply the
   *  HSE filter (incident_count trackers are unscored and must be excluded
   *  from assessment/data-entry surfaces). null for legacy rows. */
  missionTypeKey: string | null;
}

export interface DerivedChecklistItem {
  /** mp_item_<index> identifier matching mission_metric_points.item_id. */
  itemId: string;
  /** Display label from kpi_config.checklist_items[<index>].label_key. */
  label: string;
  /** True when the latest item_id-bearing point's value >= 1. */
  checked: boolean;
}

/* ── checklist_items JSONB shape (definition side) ──
 * Stored as JSONB on kpi_config.checklist_items, opaque-string at the
 * API boundary. Shape (post-DEV-1105):
 *   [
 *     {"label_key": "...", "position": 0},
 *     {"label_key": "...", "position": 1},
 *     ...
 *   ]
 *
 * position is the STABLE IDENTITY of an item. It anchors
 * mission_metric_points.item_id (mp_item_<position>) and never reused
 * or renumbered on delete/reorder. Array order = display order, which
 * is independent of position.
 *
 * Legacy rows (pre-DEV-1105) may carry only label_key — the reader
 * falls back to array index for itemId derivation. Older still rows
 * may carry a 'checked' boolean we ignore (authoritative state lives
 * in mission_metric_points).
 */
interface ChecklistItemDefinition {
  label_key?: string;
  /** Stable identity (DEV-1105). Falls back to array index when missing
   *  (legacy rows). */
  position?: number;
  // legacy fields — ignored on read post-DEV-1075:
  checked?: boolean;
  [k: string]: unknown;
}

/**
 * Convert a list of unified KpiConfig + MissionMetricPoint rows into
 * the goal-view array a Mini-Project's Mission section consumes.
 *
 * - Filters configs to category='mission' + is_active=true.
 * - Groups points by sub_kpi_key (= instance_guid as text).
 * - Derives currentValue from the latest value-bearing point (or 0).
 * - For checklist primitives, materializes per-item state from
 *   item_id-bearing points (latest value >= 1 per item_id).
 * - Sorts by kpi_config.position, then created_at.
 */
export function kpiConfigsToGoalViews(
  configs: KpiConfigResponse[],
  points: MissionMetricPointResponse[],
): MiniProjectGoalView[] {
  // Index points by sub_kpi_key for cheap lookup.
  const pointsByKey = new Map<string, MissionMetricPointResponse[]>();
  for (const p of points) {
    const key = p.subKpiKey;
    if (!pointsByKey.has(key)) pointsByKey.set(key, []);
    pointsByKey.get(key)!.push(p);
  }

  const views: MiniProjectGoalView[] = [];

  for (const cfg of configs) {
    if (cfg.category !== 'mission') continue;
    if (!cfg.isActive) continue;
    if (!cfg.miniProjectGuid) continue;

    const primitive = inferPrimitiveFromUnified(
      cfg.missionTypeKey,
      cfg.uom,
    );

    // Sub-key for mission_metric_points = instance_guid as text.
    // Match case-insensitively — server returns uppercase GUIDs in some
    // paths, lowercase in others.
    const skey = cfg.instanceGuid;
    const slot =
      pointsByKey.get(skey)
      ?? pointsByKey.get(skey.toLowerCase())
      ?? pointsByKey.get(skey.toUpperCase())
      ?? [];

    // Derive currentValue from latest value-bearing point (item-less,
    // for non-checklist; for checklist we count checked items instead).
    let currentValue = 0;
    let latestPointAt: string | null = null;
    if (primitive === 'checklist') {
      // Count items whose latest value >= 1.
      const byItem = new Map<string, MissionMetricPointResponse>();
      for (const p of slot) {
        if (!p.itemId) continue;
        const prior = byItem.get(p.itemId);
        if (!prior || (p.reportingDate ?? '') > (prior.reportingDate ?? '')) {
          byItem.set(p.itemId, p);
        }
      }
      let completed = 0;
      for (const row of byItem.values()) {
        if ((row.value ?? 0) >= 1) completed++;
        if (!latestPointAt || (row.createdAt ?? '') > latestPointAt) {
          latestPointAt = row.createdAt;
        }
      }
      currentValue = completed;
    } else {
      // Non-checklist: latest value-bearing point's value wins.
      const valueBearing = slot.filter(
        (p) => p.value !== null && p.value !== undefined && !p.itemId,
      );
      if (valueBearing.length > 0) {
        const latest = valueBearing.reduce(
          (best, p) => (!best || (p.reportingDate ?? '') > (best.reportingDate ?? '') ? p : best),
          null as MissionMetricPointResponse | null,
        );
        currentValue = latest?.value ?? 0;
        latestPointAt = latest?.createdAt ?? null;
      }
    }

    // Materialize derived checklist when primitive=checklist.
    let derivedChecklist: DerivedChecklistItem[] | null = null;
    if (primitive === 'checklist' && cfg.checklistItems) {
      try {
        const defs = JSON.parse(cfg.checklistItems) as ChecklistItemDefinition[];
        // Build a quick lookup of latest value per item_id.
        const checkedByItem = new Map<string, boolean>();
        for (const p of slot) {
          if (!p.itemId) continue;
          const prior = checkedByItem.get(p.itemId);
          const isChecked = (p.value ?? 0) >= 1;
          // Latest wins — track via reportingDate
          // (simpler than re-grouping; the byItem above already did
          // the work, but we redo it here so the value/key access is
          // local to this block).
          if (prior === undefined) {
            checkedByItem.set(p.itemId, isChecked);
          } else {
            // We're not tracking dates again here; if a later point
            // existed it would have already overwritten on the
            // byItem path above. For safety: latest stamp wins via
            // the same iteration order, since slot is fetched in
            // reporting_date ASC order from the API.
            checkedByItem.set(p.itemId, isChecked);
          }
        }
        // DEV-1105 — derive item_id from def.position (stable identity).
        // Legacy rows without explicit position fall back to array index;
        // once edited through the GoalEditTray on/after DEV-1105 they
        // graduate to explicit positions on the next save.
        derivedChecklist = defs.map((def, idx) => {
          const position = typeof def.position === 'number' ? def.position : idx;
          const itemId = `mp_item_${position}`;
          return {
            itemId,
            label: def.label_key ?? '',
            checked: checkedByItem.get(itemId) ?? false,
          };
        });
      } catch {
        // Malformed JSONB — render as empty checklist rather than crash.
        derivedChecklist = [];
      }
    }

    // currencyCode lives in uom for currency primitives.
    const currencyCode = primitive === 'currency' ? (cfg.uom ?? null) : null;
    const unitKey =
      primitive === 'currency'
        ? null  // currency code is not the same as a unit-key i18n label
        : (cfg.uom ?? null);

    // lastUpdatedAt = max(config.updatedAt, latest point createdAt).
    const lastUpdatedAt =
      latestPointAt && latestPointAt > cfg.updatedAt
        ? latestPointAt
        : cfg.updatedAt;

    // DEV-1116: Derive latest qualitative assessment for the Mission KPI
    // dot aggregation. Take the latest reportingDate among points that
    // carry an .assessment value (qualitative-bearing points). Item-id
    // bearing rows can also carry assessments but for checklist primitive
    // the dot still reads goal-level assessment, not per-item.
    let latestAssessment: string | null = null;
    let latestAssessmentDate: string | null = null;
    for (const p of slot) {
      if (!p.assessment) continue;
      if (
        latestAssessmentDate === null
        || (p.reportingDate ?? '') > latestAssessmentDate
      ) {
        latestAssessment = p.assessment;
        latestAssessmentDate = p.reportingDate ?? '';
      }
    }

    views.push({
      goalGuid: cfg.instanceGuid,
      miniProjectGuid: cfg.miniProjectGuid,
      kpiConfigId: cfg.id,
      nameKey: cfg.displayName,
      primitive,
      unitKey,
      targetValue: cfg.targetGoal ?? null,
      currentValue,
      required: cfg.isMandatory,
      checklistItems: cfg.checklistItems,
      derivedChecklist,
      position: cfg.position,
      currencyCode,
      lastUpdatedAt,
      createdAt: cfg.createdAt,
      updatedAt: cfg.updatedAt,
      // DEV-1075 Phase 7.3a — passed through for the edit tray.
      descriptionRich: cfg.descriptionRich,
      descriptionPlain: cfg.descriptionPlain,
      descriptionPhotos: cfg.descriptionPhotos,
      includedInRollup: cfg.includedInRollup,
      deadlineDate: cfg.deadlineDate,
      latestAssessment,
      missionTypeKey: cfg.missionTypeKey ?? null,
    });
  }

  // Sort by position, then createdAt for stable ordering.
  views.sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    return a.createdAt.localeCompare(b.createdAt);
  });

  return views;
}
